// Contact Information Section Component - Simplified using localStorage
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import {
    FiUser,
    FiMail,
    FiPhone,
    FiMapPin,
    FiCalendar,
    FiRefreshCw
} from 'react-icons/fi';
import {
    MdApartment
} from 'react-icons/md';

const ContactInformationSection = forwardRef(({ onDataUpdate, initialData, overrideUser }, ref) => {
    // When called from the admin dashboard modal, overrideUser contains the selected
    // customer's data. When called from the public quote flow, it is undefined and we
    // fall back to the logged-in user in localStorage as before.
    const storedUser = overrideUser || JSON.parse(localStorage.getItem('user') || '{}');
    
    // Extract user data and profile
    const user = {
        id: storedUser.id,
        username: storedUser.username,
        email: storedUser.email,
        name: storedUser.name || storedUser.username,
        first_name: storedUser.first_name,
        last_name: storedUser.last_name,
        full_name: storedUser.full_name,
        profile: storedUser.profile || {
            phone: '',
            address: '',
            company_name: '',
            contact_person: ''
        }
    };
    
    // Check if profile has phone/address data
    const hasProfilePhone = !!user.profile?.phone;
    const hasProfileAddress = !!user.profile?.address;
    
    // Initialize state with user profile data
    const [formData, setFormData] = useState({
        name: initialData?.name || user.full_name || user.name || '',
        email: initialData?.email || user.email || '',
        phone: initialData?.phone || user.profile?.phone || '',
        address: initialData?.address || user.profile?.address || '',
        preferredContact: initialData?.preferredContact || 'phone',
        bestTime: initialData?.bestTime || 'morning',
        appointmentDate: initialData?.appointmentDate || ''
    });
    
    // Track if profile data is loaded
    const [hasProfileData, setHasProfileData] = useState(hasProfilePhone || hasProfileAddress);
    const [isFetching, setIsFetching] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [fetchSuccess, setFetchSuccess] = useState(false);

    // Field-level validation errors
    const [fieldErrors, setFieldErrors] = useState({});

    // Expose validate() to parent via ref
    useImperativeHandle(ref, () => ({
        validate() {
            const errors = {};
            if (!formData.name?.trim()) errors.name = 'Full name is required.';
            if (!formData.email?.trim()) errors.email = 'Email address is required.';
            if (!formData.phone?.trim()) errors.phone = 'Phone number is required.';
            if (!formData.address?.trim()) errors.address = 'Property address is required.';
            if (!formData.appointmentDate) errors.appointmentDate = 'Preferred date is required.';
            setFieldErrors(errors);
            return Object.keys(errors).length === 0;
        }
    }));

    useEffect(() => {
        if (onDataUpdate) {
            onDataUpdate(formData);
        }
    }, [formData, onDataUpdate]);

    const handleChange = (e) => {
        // Only allow changes for fields that are not locked by profile data
        const fieldName = e.target.name;
        
        // Check if the field should be editable
        if (
            (fieldName === 'phone' && hasProfilePhone) ||
            (fieldName === 'address' && hasProfileAddress) ||
            (fieldName === 'name' && !!user.email) ||
            (fieldName === 'email' && !!user.email)
        ) {
            return; // Don't update if field is locked
        }
        
        setFormData({
            ...formData,
            [fieldName]: e.target.value
        });
        // Clear validation error for this field as the user types
        if (fieldErrors[fieldName]) {
            setFieldErrors(prev => { const n = { ...prev }; delete n[fieldName]; return n; });
        }
    };

    // Fetch latest profile from the API, persist to localStorage, then update form
    const refreshProfileData = async () => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            setFetchError('You are not logged in. Please log in and try again.');
            return;
        }

        setIsFetching(true);
        setFetchError('');
        setFetchSuccess(false);

        try {
            const API_URL = process.env.REACT_APP_API_URL;

            const response = await axios.get(`${API_URL}/rest-auth/user-profile/details/`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const freshUser = response.data;

            // Merge fresh data into the existing stored user object and persist
            const existingStored = JSON.parse(localStorage.getItem('user') || '{}');
            const mergedUser = {
                ...existingStored,
                ...freshUser,
                profile: {
                    ...existingStored.profile,
                    ...freshUser.profile
                }
            };
            localStorage.setItem('user', JSON.stringify(mergedUser));

            // Update form fields with the fresh data
            const newHasPhone = !!freshUser.profile?.phone;
            const newHasAddress = !!freshUser.profile?.address;

            setFormData(prev => ({
                ...prev,
                name: freshUser.full_name || freshUser.name || freshUser.username || prev.name,
                email: freshUser.email || prev.email,
                phone: freshUser.profile?.phone || prev.phone,
                address: freshUser.profile?.address || prev.address
            }));

            setHasProfileData(newHasPhone || newHasAddress);
            setFetchSuccess(true);

            // Clear success message after 3 seconds
            setTimeout(() => setFetchSuccess(false), 3000);

        } catch (err) {
            const status = err.response?.status;
            if (status === 401) {
                setFetchError('Session expired. Please log in again.');
            } else if (status === 500) {
                setFetchError('Server error. Please try again later.');
            } else {
                setFetchError('Could not fetch profile. Check your connection and try again.');
            }
        } finally {
            setIsFetching(false);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-lg p-8" data-aos="fade-up">
            <div className="flex justify-between items-center mb-8">
                <div className="flex items-center">
                    <div className="p-3 bg-purple-100 rounded-lg mr-4">
                        <FiUser className="text-3xl text-purple-600" />
                    </div>
                    <div>
                        <h2 className="text-3xl font-bold text-gray-800">Contact Information</h2>
                        <p className="text-gray-600">Review and update your contact details for your free consultation</p>
                    </div>
                </div>
                
                {/* Only show the refresh button in the self-service flow (not when admin is
                    filling in on behalf of a customer via overrideUser). */}
                {!overrideUser && (
                <div className="flex flex-col items-end gap-2">
                    <button
                        type="button"
                        onClick={refreshProfileData}
                        disabled={isFetching}
                        className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                            isFetching
                                ? 'bg-blue-50 text-blue-400 cursor-not-allowed'
                                : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                        }`}
                    >
                        <FiRefreshCw className={`mr-2 ${isFetching ? 'animate-spin' : ''}`} />
                        {isFetching ? 'Fetching...' : 'Refresh from Profile'}
                    </button>
                    {fetchSuccess && (
                        <p className="text-sm text-green-600 font-medium">
                            ✓ Profile refreshed successfully
                        </p>
                    )}
                    {fetchError && (
                        <p className="text-sm text-red-600 font-medium">
                            {fetchError}
                        </p>
                    )}
                </div>
                )}
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">
                            <div className="flex items-center mb-1">
                                <FiUser className="mr-2" />
                                Full Name *
                            </div>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className={`w-full px-4 py-3 border-2 rounded-lg transition-all ${!!user.email 
                                ? 'border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed' 
                                : fieldErrors.name
                                    ? 'border-red-500 focus:ring-2 focus:ring-red-400 focus:border-red-500'
                                    : 'border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500'}`}
                            placeholder="John Doe"
                            disabled={!!user.email}
                        />
                        {fieldErrors.name && (
                            <p className="text-sm text-red-600 mt-1 font-medium">⚠ {fieldErrors.name}</p>
                        )}
                        {user.email && (
                            <p className="text-sm text-green-600 mt-1">
                                <FiUser className="inline mr-1" />
                                This name is from your account. Contact support to change it.
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-2">
                            <div className="flex items-center mb-1">
                                <FiMail className="mr-2" />
                                Email Address *
                            </div>
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className={`w-full px-4 py-3 border-2 rounded-lg transition-all ${!!user.email 
                                ? 'border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed' 
                                : fieldErrors.email
                                    ? 'border-red-500 focus:ring-2 focus:ring-red-400 focus:border-red-500'
                                    : 'border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500'}`}
                            placeholder="john@example.com"
                            disabled={!!user.email}
                        />
                        {fieldErrors.email && (
                            <p className="text-sm text-red-600 mt-1 font-medium">⚠ {fieldErrors.email}</p>
                        )}
                        {user.email && (
                            <p className="text-sm text-green-600 mt-1">
                                <FiMail className="inline mr-1" />
                                This email is from your account. Contact support to change it.
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-2">
                            <div className="flex items-center mb-1">
                                <FiPhone className="mr-2" />
                                Phone Number *
                            </div>
                        </label>
                        <div className="relative">
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                                className={`w-full px-4 py-3 border-2 rounded-lg transition-all ${hasProfilePhone 
                                    ? 'border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed' 
                                    : fieldErrors.phone
                                        ? 'border-red-500 focus:ring-2 focus:ring-red-400 focus:border-red-500'
                                        : 'border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500'}`}
                                placeholder="+268 76 123 4567"
                                disabled={hasProfilePhone}
                            />
                            {fieldErrors.phone && (
                                <p className="text-sm text-red-600 mt-1 font-medium">⚠ {fieldErrors.phone}</p>
                            )}
                            {hasProfilePhone && (
                                <div className="absolute right-3 top-3">
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                        From Profile
                                    </span>
                                </div>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                            {hasProfilePhone 
                                ? "This phone number is from your saved profile. Contact support to change it."
                                : "Enter your phone number. Save it in your profile for future use."}
                        </p>
                    </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">
                            <div className="flex items-center mb-1">
                                <FiMapPin className="mr-2" />
                                Property Address *
                            </div>
                        </label>
                        <div className="relative">
                            <textarea
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                required
                                className={`w-full h-32 px-4 py-3 border-2 rounded-lg transition-all ${hasProfileAddress 
                                    ? 'border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed resize-none' 
                                    : fieldErrors.address
                                        ? 'border-red-500 focus:ring-2 focus:ring-red-400 focus:border-red-500'
                                        : 'border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500'}`}
                                placeholder="Full address including city and postal code"
                                disabled={hasProfileAddress}
                            />
                            {fieldErrors.address && (
                                <p className="text-sm text-red-600 mt-1 font-medium">⚠ {fieldErrors.address}</p>
                            )}
                            {hasProfileAddress && (
                                <div className="absolute right-3 top-3">
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                        From Profile
                                    </span>
                                </div>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                            {hasProfileAddress 
                                ? "This address is from your saved profile. Contact support to change it."
                                : "Enter your property address. Save it in your profile for future use."}
                        </p>
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-4">Preferred Contact Method</label>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { id: 'phone', label: 'Phone Call' },
                                { id: 'email', label: 'Email' },
                                { id: 'whatsapp', label: 'WhatsApp' },
                                { id: 'sms', label: 'SMS' }
                            ].map(method => (
                                <label key={method.id} className="relative">
                                    <input
                                        type="radio"
                                        name="preferredContact"
                                        value={method.id}
                                        checked={formData.preferredContact === method.id}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className={`p-4 border-2 rounded-lg text-center cursor-pointer transition-all ${formData.preferredContact === method.id
                                        ? 'border-purple-500 bg-purple-50'
                                        : 'border-gray-300 hover:border-purple-300'
                                        }`}>
                                        <div className="font-medium text-gray-800">{method.label}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-gray-700 font-medium mb-2">
                                <div className="flex items-center mb-1">
                                    <FiCalendar className="mr-2" />
                                    Best Time to Contact
                                </div>
                            </label>
                            <select
                                name="bestTime"
                                value={formData.bestTime}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                            >
                                <option value="morning">Morning (8am-12pm)</option>
                                <option value="afternoon">Afternoon (12pm-4pm)</option>
                                <option value="evening">Evening (4pm-8pm)</option>
                                <option value="anytime">Anytime</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-gray-700 font-medium mb-2">
                                <div className="flex items-center mb-1">
                                    <MdApartment className="mr-2" />
                                    Preferred Date *
                                </div>
                            </label>
                            <input
                                type="date"
                                name="appointmentDate"
                                value={formData.appointmentDate}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-lg transition-all ${
                                    fieldErrors.appointmentDate
                                        ? 'border-red-500 focus:ring-2 focus:ring-red-400 focus:border-red-500'
                                        : 'border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500'
                                }`}
                                min={new Date().toISOString().split('T')[0]}
                            />
                            {fieldErrors.appointmentDate && (
                                <p className="text-sm text-red-600 mt-1 font-medium">⚠ {fieldErrors.appointmentDate}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start">
                    <div className="p-2 bg-blue-100 rounded-lg mr-3">
                        <FiUser className="text-blue-600" />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-800">Using Your Account Information</h3>
                        <p className="text-sm text-gray-600 mb-2">
                            Your name and email are automatically filled from your account. 
                            {hasProfileData 
                                ? " Phone number and address are loaded from your saved profile and cannot be changed here."
                                : " Save your phone number and address in your profile for future use."}
                        </p>
                        <p className="text-sm text-gray-600">
                            {hasProfileData 
                                ? "Contact support to update your profile information."
                                : "You can enter your phone and address here for this specific quotation."}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
});

ContactInformationSection.displayName = 'ContactInformationSection';

export default ContactInformationSection;